/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useState} from 'react';

/**
 * Provides a hook which measures the live height, in pixels, of the given
 * ESRI `MapView`'s own container.
 *
 * This is used to size portal-based map widget panels relative to the
 * map's currently available height, so a panel can span the map area
 * (minus fixed margins) and grow/shrink as a resizable grid container
 * changes how much vertical space the map has.
 *
 * ```typescript
 *
 *   import {useMapViewHeight} from '@ion/features/src/maps/hooks/useMapViewHeight';
 *
 *   const mapViewHeight = useMapViewHeight(view);
 *
 * ```
 *
 * @param   view The ESRI `MapView` whose own container's height is
 *          measured.
 * @returns The live height, in pixels, of the map's own container, or
 *          `null` until the first measurement completes.
 */
export const useMapViewHeight = (view: __esri.MapView): number | null => {
  const [height, setHeight] = useState<number | null>(null);

  useEffect(() => {
    const updateHeight = function() {
      const rect = view?.container?.getBoundingClientRect?.();
      if (rect) {
        setHeight(Math.max(rect.height, 0));
      }
    };

    updateHeight();

    const target = view?.container;
    const observer = target ? new ResizeObserver(updateHeight) : null;
    observer?.observe(target);
    window.addEventListener('resize', updateHeight);

    return function() {
      observer?.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, [view]);

  return height;
};

export default useMapViewHeight;
